import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar,
  User,
  ChevronDown,
  ChevronUp,
  Bell,
  Cpu,
  Shield,
  Database,
  CircuitBoard,
  Activity,
  RefreshCw,
  Compass
} from 'lucide-react';

const News = () => {
  const [expandedPosts, setExpandedPosts] = useState(new Set());
  const [selectedCategory, setSelectedCategory] = useState('all');

  // ALL POSTS ARE DEFINED HERE - EDIT THIS ARRAY TO ADD/REMOVE/MODIFY POSTS
  const posts = [
    {
      id: 1,
      title: "AUKUS Maritime Innovation Challenge Participation",
      content: "Reticuli Technologies submitted a proposal to the AUKUS Maritime Innovation Challenge 2025, a collaborative initiative between the U.S. Defense Innovation Unit (DIU), Australia's ASCA, and the UK's DASA focused on advancing undersea communications and autonomous systems.\n\nThis challenge represents a significant opportunity to contribute to allied maritime capabilities through cutting-edge technology development. The competition seeks innovative solutions for next-generation underwater operations, emphasizing autonomous coordination and reliable communications in challenging maritime environments.\n\nWe're excited to support the AUKUS Pillar II innovation framework and contribute to strengthening allied naval capabilities through advanced technological solutions.",
      category: "partnerships",
      date: new Date('2025-04-28'),
      author: "Reticuli Technologies"
    },
    {
      id: 5,
      title: "Reticuli Technologies Accepted into the NVIDIA Inception Program",
      content: "Reticuli Technologies has been accepted into NVIDIA's Inception program.",
      category: "partnerships",
      date: new Date('2026-02-23'),
      author: "Reticuli Technologies"
    },
    // {
    //   id: 4,
    //   title: "Formation of Unity Lodge No. Δ",
    //   content: "We are honored to announce the formation of Unity Lodge No. Δ, a new Masonic lodge dedicated to the principles of universal brotherhood, technological advancement, and the pursuit of light.\n\nUnity Lodge No. Δ represents a vision for Freemasonry that embraces both ancient wisdom and modern innovation. The lodge will serve as a gathering place for Brethren who share a commitment to intellectual growth, charitable service, and the continuous improvement of humanity through the application of Masonic principles.\n\nThe delta symbol (Δ) was chosen to represent change, transformation, and the triangular foundations of Masonic philosophy. As we work to establish this new lodge, we invite interested Master Masons to learn more about our mission and vision.\n\nMore information will be made available as we progress through the formation process.",
    //   category: "masonic",
    //   date: new Date('2025-01-10'),
    //   author: "Founder & CEO"
    // }
  ];

  const categories = [
    { id: 'all', name: 'All Updates', icon: <Database size={16} /> },
    { id: 'development', name: 'Development', icon: <Cpu size={16} /> },
    { id: 'company', name: 'Company News', icon: <Bell size={16} /> },
    { id: 'research', name: 'Research', icon: <CircuitBoard size={16} /> },
    { id: 'security', name: 'Security', icon: <Shield size={16} /> },
    { id: 'partnerships', name: 'Partnerships', icon: <Activity size={16} /> },
    // { id: 'masonic', name: 'Masonic', icon: <Compass size={16} /> }
  ];

  // Sort posts by date (newest first)
  const sortedPosts = [...posts].sort((a, b) => new Date(b.date) - new Date(a.date));

  const getCategoryIcon = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.icon : <Database size={16} />;
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Unknown';
  };

  const togglePostExpansion = (postId) => {
    const newExpanded = new Set(expandedPosts);
    if (newExpanded.has(postId)) {
      newExpanded.delete(postId);
    } else {
      newExpanded.add(postId);
    }
    setExpandedPosts(newExpanded);
  };

  const formatContent = (content, isExpanded) => {
    const paragraphs = content.split('\n').filter(p => p.trim());
    
    if (!isExpanded) {
      const firstParagraph = paragraphs[0];
      return firstParagraph.length > 150 
        ? firstParagraph.substring(0, 150) + '...'
        : firstParagraph;
    }
    
    return paragraphs.map((paragraph, index) => (
      <p key={index} className="mb-3 last:mb-0">
        {paragraph}
      </p>
    ));
  };

  const filteredPosts = selectedCategory === 'all' 
    ? sortedPosts 
    : sortedPosts.filter(post => post.category === selectedCategory);

  // Advanced background components
  const TechGrid = () => (
    <div className="absolute inset-0 z-0 overflow-hidden">
      <div className="absolute w-full h-full grid grid-cols-16 gap-px opacity-5">
        {[...Array(16)].map((_, i) => (
          <motion.div 
            key={i} 
            className="border-r border-gray-400/20 h-full"
            animate={{
              opacity: [0.05, 0.15, 0.05],
              borderColor: ["rgba(156, 163, 175, 0.05)", "rgba(156, 163, 175, 0.15)", "rgba(156, 163, 175, 0.05)"]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              delay: i * 0.2
            }}
          />
        ))}
      </div>
    </div>
  );

  const ScanLine = () => (
    <motion.div
      className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-400/10 to-transparent h-32 pointer-events-none"
      animate={{
        top: ["-20%", "120%"],
      }}
      transition={{
        duration: 8,
        repeat: Infinity,
        ease: "linear"
      }}
    />
  );

  const HolographicText = ({ children, className = "" }) => (
    <motion.div
      className={`relative ${className}`}
      animate={{
        textShadow: [
          "0 0 5px rgba(156, 163, 175, 0.5)",
          "0 0 10px rgba(156, 163, 175, 0.8), 0 0 15px rgba(156, 163, 175, 0.5)",
          "0 0 5px rgba(156, 163, 175, 0.5)"
        ]
      }}
      transition={{
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }}
    >
      {children}
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-400/20 to-transparent"
        animate={{
          x: ["-100%", "100%"]
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "linear",
          delay: Math.random() * 3
        }}
      />
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <TechGrid />
      <ScanLine />
      
      {/* Header */}
      <motion.div 
        className="relative z-10 pt-20 pb-16"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-8">
            <div>
              <HolographicText className="text-5xl md:text-7xl font-bold text-gray-100 mb-4">
                News & Updates
              </HolographicText>
              <p className="text-xl text-gray-400">Latest developments from Reticuli Technologies</p>
            </div>
          </div>
          
          {/* Category Filter */}
          <motion.div 
            className="flex flex-wrap gap-4 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {categories.map((category) => (
              <motion.button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center px-4 py-2 rounded-lg border transition-all duration-300 ${
                  selectedCategory === category.id
                    ? 'bg-gray-600 border-gray-400 text-gray-100'
                    : 'bg-gray-900/50 border-gray-600 text-gray-400 hover:bg-gray-800/50 hover:border-gray-500'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {category.icon}
                <span className="ml-2 text-sm font-medium">{category.name}</span>
              </motion.button>
            ))}
          </motion.div>
        </div>
      </motion.div>

      {/* Posts Grid */}
      <div className="container mx-auto px-4 relative z-10 pb-20">
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          layout
        >
          <AnimatePresence>
            {filteredPosts.map((post, index) => {
              const isExpanded = expandedPosts.has(post.id);
              const hasMoreContent = post.content.length > 150 || post.content.includes('\n');
              
              return (
                <motion.div
                  key={post.id}
                  layout
                  initial={{ opacity: 0, y: 50, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -50, scale: 0.9 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -10, scale: 1.02 }}
                  className="bg-gradient-to-br from-gray-900/90 to-black/90 border border-gray-500/30 rounded-xl p-6 backdrop-blur-sm relative overflow-hidden group"
                >
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-br from-gray-500/5 to-gray-300/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    animate={{
                      background: [
                        "linear-gradient(135deg, rgba(156,163,175,0.02), rgba(209,213,219,0.02))",
                        "linear-gradient(225deg, rgba(209,213,219,0.02), rgba(156,163,175,0.02))",
                        "linear-gradient(135deg, rgba(156,163,175,0.02), rgba(209,213,219,0.02))"
                      ]
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                  />
                  
                  <div className="relative z-10">
                    <div className="flex justify-between items-start mb-4">
                      <motion.div 
                        className="flex items-center px-3 py-1 bg-gray-800/60 rounded-full border border-gray-600/50"
                        whileHover={{ scale: 1.1 }}
                      >
                        {getCategoryIcon(post.category)}
                        <span className="ml-2 text-xs font-medium text-gray-300 uppercase tracking-wider">
                          {getCategoryName(post.category)}
                        </span>
                      </motion.div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-100 mb-3 group-hover:text-gray-50 transition-colors duration-300">
                      {post.title}
                    </h3>
                    
                    <motion.div 
                      className="text-gray-400 mb-4 leading-relaxed"
                      layout
                      animate={{ height: 'auto' }}
                      transition={{ duration: 0.3 }}
                    >
                      {formatContent(post.content, isExpanded)}
                    </motion.div>
                    
                    {hasMoreContent && (
                      <motion.button
                        onClick={() => togglePostExpansion(post.id)}
                        className="flex items-center text-sm text-gray-500 hover:text-gray-300 transition-colors duration-200 mb-4"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {isExpanded ? (
                          <>
                            <ChevronUp size={16} className="mr-1" />
                            Show less
                          </>
                        ) : (
                          <>
                            <ChevronDown size={16} className="mr-1" />
                            Read more
                          </>
                        )}
                      </motion.button>
                    )}
                    
                    <div className="flex justify-between items-center text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar size={14} className="mr-2" />
                        {post.date.toLocaleDateString()}
                      </div>
                      <div className="flex items-center">
                        <User size={14} className="mr-2" />
                        {post.author}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
        
        {filteredPosts.length === 0 && (
          <motion.div 
            className="text-center py-20"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <Database size={48} className="mx-auto mb-4 text-gray-600" />
            <h3 className="text-xl font-semibold text-gray-400 mb-2">No posts found</h3>
            <p className="text-gray-500">No posts in this category yet.</p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default News;